import { ICONS_TYPES } from '@components/Icon';
import { isDefAndNotNull } from '@utils/def';
import moment from 'moment';
import { ReactNode } from 'react';
import { FieldErrors } from 'react-hook-form';
import { FieldError } from 'react-hook-form/dist/types/errors';

import { HedgingInstrumentInterestRateMode } from '../../models/HedgingInstrument/enum';
import { LegTypes } from '../../models/Leg/enum';
import { LegType } from '../../models/LegType';

import {
  ColorModes,
  PricerModes,
  TradePageLinkKeys,
  TransactionReviewStatusModes,
} from './enums';
import { TransactionTypeModes } from './enums/TransactionTypeModes';
import {
  ValidationErrorText,
  ValidationErrorTitle,
  ValidationHeaderError,
} from './styles';
import {
  TableValidationRefType,
  TableValidationType,
  TradePageLinkItem,
} from './types';

export const TransactionReviewStatusColors: Record<
  TransactionReviewStatusModes,
  ColorModes
> = {
  [TransactionReviewStatusModes.PENDING]: ColorModes.warning,
  [TransactionReviewStatusModes.ACCEPTED]: ColorModes.success,
  [TransactionReviewStatusModes.AMEND]: ColorModes.warning,
  [TransactionReviewStatusModes.REFUSED]: ColorModes.error,
  [TransactionReviewStatusModes.PROBLEMATIC]: ColorModes.warning,
  [TransactionReviewStatusModes.REVIEWED]: ColorModes.success,
  [TransactionReviewStatusModes['PENDING CLOSE']]: ColorModes.orange,
};

export const TransactionReviewStatusLabelText: Record<
  TransactionReviewStatusModes,
  string | undefined
> = {
  [TransactionReviewStatusModes.PENDING]: 'Pending',
  [TransactionReviewStatusModes.ACCEPTED]: undefined,
  [TransactionReviewStatusModes.AMEND]: 'Amendment',
  [TransactionReviewStatusModes.REFUSED]: 'Rejected',
  [TransactionReviewStatusModes.PROBLEMATIC]: 'Rejected',
  [TransactionReviewStatusModes.REVIEWED]: undefined,
  [TransactionReviewStatusModes['PENDING CLOSE']]: 'Pending Close',
};

export const TransactionTypeColors: Record<TransactionTypeModes, ColorModes> = {
  [TransactionTypeModes.Actual]: ColorModes.success,
  [TransactionTypeModes.Hypothetical]: ColorModes.violet,
  [TransactionTypeModes.Test]: ColorModes.gray,
  [TransactionTypeModes.Valuation]: ColorModes.gray,
  [TransactionTypeModes.Deleted]: ColorModes.error,
  [TransactionTypeModes.Internal]: ColorModes.blue,
  [TransactionTypeModes.Component]: ColorModes.pinkGraph,
  [TransactionTypeModes.TradeRequest]: ColorModes.pinkGraph,
};

export const TransactionTypeLabelText: Record<
  TransactionTypeModes,
  string | undefined
> = {
  [TransactionTypeModes.Actual]: 'Live',
  [TransactionTypeModes.Hypothetical]: 'Hypo',
  [TransactionTypeModes.Test]: undefined,
  [TransactionTypeModes.Valuation]: 'Draft',
  [TransactionTypeModes.Deleted]: 'Deleted',
  [TransactionTypeModes.Internal]: 'Draft',
  [TransactionTypeModes.Component]: 'Component',
  [TransactionTypeModes.TradeRequest]: 'FL Workflow',
};

export const AddLegsForPricerList: Record<PricerModes, LegTypes[]> = {
  [PricerModes.interestRate]: [
    LegTypes.fixed,
    LegTypes.float,
    LegTypes.cap,
    LegTypes.floor,
    LegTypes.scheduledPayments,
  ],
  [PricerModes.fx]: [],
  [PricerModes.commodity]: [
    LegTypes.scheduledPayments,
    LegTypes.commodityFixed,
    LegTypes.commodityFloat,
    LegTypes.commodityCall,
    LegTypes.commodityPut,
  ],
  [PricerModes.inflation]: [
    LegTypes.fixed,
    LegTypes.float,
    LegTypes.inflation,
    LegTypes.compoundingFixed,
    LegTypes.payGo,
  ],
  [PricerModes.debt]: [],
  [PricerModes.custom]: [
    LegTypes.fixed,
    LegTypes.float,
    LegTypes.cap,
    LegTypes.floor,
    LegTypes.inflation,
    LegTypes.forward,
    LegTypes.scheduledPayments,
    LegTypes.commodityFixed,
    LegTypes.payGo,
    LegTypes.windowedForward,
    LegTypes.upfrontPremium,
    LegTypes.commodityFixed,
    LegTypes.commodityFloat,
    LegTypes.commodityCall,
    LegTypes.commodityPut,
  ],
};

export const getTransactionTypeName = (
  legTypesList: LegType[],
  legTypeId: LegTypes,
) => legTypesList.find((leg) => leg.leg_type_id === legTypeId)?.type;

export const getCurrentNotionalValueForLeg = (
  instrumentType: HedgingInstrumentInterestRateMode,
  legTypeId: LegTypes,
  legNumber: number,
): boolean => {
  switch (instrumentType) {
    case HedgingInstrumentInterestRateMode.irSwap:
    case HedgingInstrumentInterestRateMode.IrSwapWithEmbeddedFloor:
      return legTypeId === LegTypes.float;
    case HedgingInstrumentInterestRateMode.irCap:
      return legTypeId === LegTypes.cap;
    case HedgingInstrumentInterestRateMode.irFloor:
      return legTypeId === LegTypes.floor;
    case HedgingInstrumentInterestRateMode.crossCurrencySwap:
      return legTypeId === LegTypes.fixed;
    case HedgingInstrumentInterestRateMode.irCollar:
    case HedgingInstrumentInterestRateMode.irCapWithFixed:
    case HedgingInstrumentInterestRateMode.irCollarWithPremium:
      return legTypeId === LegTypes.cap;
    case HedgingInstrumentInterestRateMode.fxForward:
    case HedgingInstrumentInterestRateMode.fxSwap:
    case HedgingInstrumentInterestRateMode.fxForwardNDF:
    case HedgingInstrumentInterestRateMode.fxOption:
    case HedgingInstrumentInterestRateMode.fxAveragingForward:
    case HedgingInstrumentInterestRateMode.fxForwardStrip:
      return legTypeId === LegTypes.scheduledPayments && legNumber === 0;
    case HedgingInstrumentInterestRateMode.inflationSwap:
      return legTypeId === LegTypes.inflation;
    case HedgingInstrumentInterestRateMode.inflationWitPayGo:
      return legTypeId === LegTypes.fixed;
    case HedgingInstrumentInterestRateMode.commoditySwap:
      return legTypeId === LegTypes.commodityFixed;
    case HedgingInstrumentInterestRateMode.commodityCollar:
    case HedgingInstrumentInterestRateMode.commodityCall:
      return legTypeId === LegTypes.commodityCall;
    case HedgingInstrumentInterestRateMode.commodityPut:
      return legTypeId === LegTypes.commodityPut;
    case HedgingInstrumentInterestRateMode.customTrade:
      return legTypeId === LegTypes.float;
    default:
      return false;
  }
};
//from trade_summary to Trade Summary
const humanizeFormFieldKey = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const collectLeafFieldErrors = (
  errors: FieldErrors,
): { fieldName: string; message: string }[] =>
  Object.entries(errors).flatMap(([key, value]) => {
    if (!value) {
      return [];
    }
    const fieldError = value as FieldError;

    if (typeof fieldError.message === 'string') {
      return [{ fieldName: key, message: fieldError.message }];
    }

    return collectLeafFieldErrors(value as FieldErrors);
  });

const renderFieldErrorGroup = (
  header: string,
  fieldErrors: { fieldName: string; message: string }[],
  groupKey: string,
): ReactNode[] => [
  <ValidationHeaderError key={`${groupKey}-header`}>
    {header}:
  </ValidationHeaderError>,
  ...fieldErrors.map(({ fieldName, message }) => (
    <ValidationErrorText key={`${groupKey}-${fieldName}`}>
      <ValidationErrorTitle>{fieldName}</ValidationErrorTitle>: {message}
    </ValidationErrorText>
  )),
];

export const getFullFormValidationResult = (
  errors: FieldErrors,
  legNames: Record<string, string> = {},
) => {
  const errorList: ReactNode[] = [];

  const legsErrors = errors.legs as
    | Record<string, { leg_terms?: FieldErrors } | undefined>
    | undefined;

  if (legsErrors) {
    Object.entries(legsErrors).forEach(([legNumber, legError]) => {
      const legTermsErrors = legError?.leg_terms;
      if (!legTermsErrors) {
        return;
      }
      const fieldErrors = collectLeafFieldErrors(legTermsErrors);
      if (!fieldErrors.length) {
        return;
      }
      const legName = legNames[legNumber] || `Leg ${Number(legNumber) + 1}`;

      errorList.push(
        ...renderFieldErrorGroup(
          `${Number(legNumber) + 1}. ${legName} Leg Terms`,
          fieldErrors,
          `legs.${legNumber}`,
        ),
      );
    });
  }

  Object.entries(errors).forEach(([key, value]) => {
    if (key === 'legs' || !value) {
      return;
    }
    const fieldError = value as FieldError;

    if (typeof fieldError.message === 'string') {
      errorList.push(
        ...renderFieldErrorGroup(
          humanizeFormFieldKey(key),
          [{ fieldName: key, message: fieldError.message }],
          key,
        ),
      );
      return;
    }

    const fieldErrors = collectLeafFieldErrors(value as FieldErrors);
    if (fieldErrors.length) {
      errorList.push(
        ...renderFieldErrorGroup(humanizeFormFieldKey(key), fieldErrors, key),
      );
    }
  });

  return { success: !errorList.length, errorList };
};

export const getValidationTableResult = (
  validationFlowsFunctions: TableValidationRefType,
) => {
  const errorList = Object.values(validationFlowsFunctions).reduce(
    (acc: ReactNode[], validation: TableValidationType) => {
      const { validationErrorState } = validation;

      if (
        isDefAndNotNull(validationErrorState) &&
        Object.keys(validationErrorState).length
      ) {
        const errorsHtml = Object.entries(validationErrorState).reduce(
          (errorAcc: ReactNode[], rowError) => {
            errorAcc.push(
              <ValidationErrorText>
                Row #{rowError[0]}:{' '}
                <ValidationErrorTitle>
                  {Object.keys(rowError[1]).join(', ')}
                </ValidationErrorTitle>{' '}
                - Fill required fields
              </ValidationErrorText>,
            );
            return errorAcc;
          },
          [],
        );

        acc.push(
          <ValidationHeaderError>
            {validation.tableName}:
          </ValidationHeaderError>,
        );
        acc.push(...errorsHtml);
      }
      return acc;
    },
    [],
  );
  const warningList = Object.values(validationFlowsFunctions).reduce(
    (acc: ReactNode[], validation: TableValidationType) => {
      const { validationWarningState } = validation;

      if (
        isDefAndNotNull(validationWarningState) &&
        Object.keys(validationWarningState).length
      ) {
        const errorsHtml = Object.entries(validationWarningState).reduce(
          (errorAcc: ReactNode[], rowError) => {
            errorAcc.push(
              <ValidationErrorText>
                Row #{rowError[0]}:{' '}
                {Object.values(rowError[1]).map((error, index) => (
                  <ValidationErrorText key={index}>
                    <ValidationErrorTitle>
                      {error?.columnName}
                    </ValidationErrorTitle>
                    : {error?.message}
                  </ValidationErrorText>
                ))}
              </ValidationErrorText>,
            );
            return errorAcc;
          },
          [],
        );

        acc.push(
          <ValidationHeaderError>
            {validation.tableName}:
          </ValidationHeaderError>,
        );
        acc.push(...errorsHtml);
      }
      return acc;
    },
    [],
  );

  return { success: !errorList.length, errorList, warningList };
};

// pages of a trade, the urls and icons are the same as in NavbarSearchPopover
export const TRADE_PAGE_LINKS: TradePageLinkItem[] = [
  {
    key: TradePageLinkKeys.je,
    label: 'JE',
    icon: ICONS_TYPES.Notebook,
    getUrl: (tradeId) => `/journal-entries?tradeIds=${tradeId}`,
  },
  {
    key: TradePageLinkKeys.haAdmin,
    label: 'HA Admin',
    icon: ICONS_TYPES.ShieldCheck,
    getUrl: (tradeId) => `/manage-hedge-accounting/${tradeId}`,
  },
  {
    key: TradePageLinkKeys.pricer,
    label: 'Pricer',
    icon: ICONS_TYPES.TrendingUp,
    getUrl: (tradeId) => `/hedging-center/pricer/${tradeId}`,
  },
  {
    key: TradePageLinkKeys.tradeDetails,
    label: 'Trade Details',
    icon: ICONS_TYPES.TransactionDollar,
    getUrl: (tradeId) => `/portfolio/transaction/${tradeId}`,
  },
];

/**
 * FX Option: true when the Fixing Date and the Maturity Date differ by more than `maxDays` days.
 * Missing or invalid dates cannot be compared, so they are not reported.
 */
export const isFixingAndMaturityDatesFarApart = (
  fixingDate: string | null | undefined,
  maturityDate: string | null | undefined,
  maxDays: number,
) => {
  if (!fixingDate || !maturityDate) return false;

  const fixing = moment(fixingDate);
  const maturity = moment(maturityDate);

  return (
    fixing.isValid() &&
    maturity.isValid() &&
    Math.abs(maturity.diff(fixing, 'day')) > maxDays
  );
};
